import React from 'react';
import { AlertTriangle, Check, RefreshCw, Search, X } from 'lucide-react';

import type { ClassificationDetail } from './api';
import { BUCKET_STYLES, URGENT_PRIORITY, asCount, asPercent, asWhen } from './constants';

/**
 * The verdict on one report.
 *
 * Deliberately shows what the classifier was GIVEN alongside what it said — the payload strips
 * images and persona tables to fit a 200,000-char limit, so "14,012 of 88,485 chars, 267 persona
 * responses removed" is often the explanation for a surprising score.
 */

interface ClassificationCardProps {
  classification: ClassificationDetail | null;
  unreliableUseCase: boolean;
  busy: boolean;
  onReclassify: () => void;
  /** Reground is offered for simulations only, and only when the backend says the report carries
   *  consumer responses to ground against. Undefined means "not a simulation" — no button. */
  regroundEligible?: boolean | null;
  regrounding?: boolean;
  onReground?: () => void;
}

/** Shown as text, not a tooltip: only 202 of 6,331 items can be audited, so an admin meets the
 *  disabled button on nearly everything and reads it as broken unless the reason is on the page. */
const REGROUND_INELIGIBLE_HINT =
  'No consumer responses to ground against — only vitamin concept tests carry them.';

const Meter: React.FC<{ label: string; value: number | null; urgent?: boolean }> = ({
  label, value, urgent,
}) => (
  <div className="min-w-[86px]">
    <div className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
    <div className="text-[15px] font-bold text-gray-900">{asPercent(value)}</div>
    <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100">
      <div
        className={`h-full rounded-full ${urgent ? 'bg-red-500' : 'bg-blue-500'}`}
        style={{ width: `${Math.round((value ?? 0) * 100)}%` }}
      />
    </div>
  </div>
);

const Observations: React.FC<{ classification: ClassificationDetail }> = ({ classification }) => {
  const supporting = classification.observations?.supporting ?? [];
  const detracting = classification.observations?.detracting ?? [];
  if (supporting.length === 0 && detracting.length === 0) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
          In its favour
        </h4>
        <ul className="space-y-1.5">
          {supporting.map((item) => (
            <li key={item} className="flex gap-2 text-[13px] text-gray-700">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-700" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
          Concerns
        </h4>
        <ul className="space-y-1.5">
          {detracting.map((item) => (
            <li key={item} className="flex gap-2 text-[13px] text-gray-700">
              <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Anchors: React.FC<{ classification: ClassificationDetail }> = ({ classification }) => {
  const anchors = classification.anchors ?? [];
  if (anchors.length === 0) return null;

  return (
    <details className="mt-4 border-t border-gray-200 pt-3">
      <summary className="cursor-pointer text-[12px] font-bold uppercase tracking-wide text-gray-600">
        Nearest human-rated reports ({anchors.length})
      </summary>
      {anchors.map((anchor, index) => (
        <div
          key={`${anchor.rating}-${anchor.similarity}-${index}`}
          className="mt-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5"
        >
          <div className="mb-1 flex flex-wrap items-center gap-2 text-[11.5px] text-gray-500">
            <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase
                              ${BUCKET_STYLES[anchor.bucket]}`}>
              {anchor.bucket}
            </span>
            <span>rated {anchor.rating}/5</span>
            <span aria-hidden="true">·</span>
            <span>{anchor.similarity.toFixed(3)} similarity</span>
          </div>
          {anchor.rater_reasoning && (
            <p className="text-[12.5px] italic text-gray-600">“{anchor.rater_reasoning}”</p>
          )}
        </div>
      ))}
    </details>
  );
};

/** Sizes and provenance — the same line under a verdict and under a failure. */
const PayloadFootnote: React.FC<{ classification: ClassificationDetail }> = ({ classification }) => (
  <span className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-gray-500">
    <span>
      {asCount(classification.raw_chars)} chars raw →{' '}
      <strong className="font-semibold">{asCount(classification.sent_chars)} sent</strong>
    </span>
    <span aria-hidden="true">·</span>
    <span>
      {asCount(classification.personas_omitted)} persona responses
      {classification.images_omitted ? `, ${asCount(classification.images_omitted)} images` : ''}{' '}
      removed
    </span>
    {classification.model_version && (
      <>
        <span aria-hidden="true">·</span>
        <span>{classification.model_version}</span>
      </>
    )}
    <span aria-hidden="true">·</span>
    <span>{asWhen(classification.classified_at)}</span>
  </span>
);

const ACTION_CLASS = 'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border '
  + 'border-gray-300 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-gray-700 '
  + 'transition-colors hover:border-blue-500 hover:text-blue-700 '
  + 'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 '
  + 'disabled:hover:text-gray-700';

const ReclassifyButton: React.FC<{ busy: boolean; onClick: () => void; label?: string }> = ({
  busy, onClick, label = 'Re-classify',
}) => (
  <button type="button" onClick={onClick} disabled={busy} className={`ml-auto ${ACTION_CLASS}`}>
    <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
    {busy ? 'Queued…' : label}
  </button>
);

/** Disabled rather than hidden when ineligible: an admin who cannot see the button cannot learn
 *  why it is missing, and the reason is the useful part. */
const RegroundButton: React.FC<{
  busy: boolean; eligible: boolean; onClick: () => void;
}> = ({ busy, eligible, onClick }) => (
  <span className="inline-flex items-center gap-2">
    {!eligible && (
      <span className="text-[11.5px] text-gray-500">{REGROUND_INELIGIBLE_HINT}</span>
    )}
    <button
      type="button"
      onClick={onClick}
      disabled={busy || !eligible}
      className={ACTION_CLASS}
    >
      <Search className={`h-3.5 w-3.5 ${busy ? 'animate-pulse' : ''}`} aria-hidden="true" />
      {busy ? 'Auditing…' : 'Reground'}
    </button>
  </span>
);

const ClassificationCard: React.FC<ClassificationCardProps> = ({
  classification, unreliableUseCase, busy, onReclassify,
  regroundEligible, regrounding = false, onReground,
}) => {
  const reground = onReground && regroundEligible != null
    ? (
      <RegroundButton
        busy={regrounding}
        eligible={regroundEligible === true}
        onClick={onReground}
      />
    )
    : null;

  if (!classification) {
    return (
      <section className="mx-6 mt-5 rounded-xl border border-gray-200 bg-white p-8 text-center">
        <p className="mb-4 text-sm text-gray-500">Not classified yet.</p>
        <div className="flex justify-center gap-3">
          <ReclassifyButton busy={busy} onClick={onReclassify} label="Classify now" />
          {reground}
        </div>
      </section>
    );
  }

  if (classification.status === 'error') {
    return (
      <section className="mx-6 mt-5 rounded-xl border border-gray-200 bg-white">
        <div className="p-5">
          <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5
                          text-[12.5px] text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              <strong className="font-semibold">Too large to classify.</strong> The classifier
              rejected this report — <code className="font-mono">{classification.error}</code>. Its
              hard limit is 200,000 characters; this report still exceeds it after images and
              persona tables were removed.
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 bg-gray-50
                        px-5 py-3">
          <PayloadFootnote classification={classification} />
          <ReclassifyButton busy={busy} onClick={onReclassify} />
          {reground}
        </div>
      </section>
    );
  }

  const urgent = (classification.review_priority ?? 0) > URGENT_PRIORITY;

  return (
    <section className="mx-6 mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white">
      <header className="flex flex-wrap items-center gap-4 border-b border-gray-200 px-5 py-4">
        <div className="flex items-baseline gap-2.5">
          {classification.bucket && (
            <span className={`rounded-lg border px-2.5 py-1 text-[12px] font-extrabold uppercase
                              tracking-wider ${BUCKET_STYLES[classification.bucket]}`}>
              {classification.bucket}
            </span>
          )}
          <span className="text-2xl font-extrabold tracking-tight text-gray-900">
            {classification.score?.toFixed(2) ?? '—'}
          </span>
          <span className="text-xs text-gray-400">/ 5</span>
        </div>
        <div className="ml-auto flex flex-wrap gap-5">
          <Meter label="Confidence" value={classification.confidence} />
          <Meter label="Review priority" value={classification.review_priority} urgent={urgent} />
        </div>
      </header>

      {unreliableUseCase && (
        <div className="mx-5 mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3
                        py-2.5 text-[12.5px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            The classifier self-reports as <strong className="font-semibold">unreliable</strong> on
            this use case — its outputs are mostly images, so little text reaches the model. Read
            the score with that in mind.
          </span>
        </div>
      )}

      {classification.warning && (
        <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5
                        text-[12.5px] text-amber-800">
          {classification.warning}
        </div>
      )}

      <div className="px-5 py-4">
        {classification.reasoning && (
          <p className="mb-4 text-[13.5px] text-gray-700">{classification.reasoning}</p>
        )}
        <Observations classification={classification} />
        <Anchors classification={classification} />
      </div>

      <footer className="flex flex-wrap items-center gap-3 border-t border-gray-200 bg-gray-50
                         px-5 py-3">
        <PayloadFootnote classification={classification} />
        <ReclassifyButton busy={busy} onClick={onReclassify} />
        {reground}
      </footer>
    </section>
  );
};

export default ClassificationCard;
